import { Link, useLocation } from 'react-router-dom'
import { IconArrowUpRight, IconInstagram, IconLinkedIn } from './ui/Icon'
import { HOME_BY_LANG, alternatesFor, normalizePath } from '../lib/routes'
import { EMAIL, MAPS_URL, PHONE_DISPLAY, PHONE_HREF } from '../data/rfq-i18n'
import { CONSENT_REOPEN_EVENT } from './CookieBanner'
import './footer.css'

// Existing footer copy (verbatim) plus the service and contact labels the
// navbar / RFQ section already use.
const translations = {
    tr: {
        companyDesc: 'Otomotiv yan sanayinde 25+ yıllık deneyim ile tampon baskı, serigrafi ve montaj çözümleri.',
        quickLinks: 'Hızlı Linkler',
        services: 'Hizmetler',
        about: 'Hakkımızda',
        corporate: 'Kurumsal',
        blog: 'Blog/Haberler',
        solutions: 'Çözümlerimiz',
        contactTitle: 'İletişim',
        address: 'Küçükbalıklı Mah. 2. Ordulu Sok. No:83',
        city: 'Osmangazi / Bursa',
        copyright: '© 2026 Ankaplastik. Tüm hakları saklıdır.',
        cookiePrefs: 'Çerez tercihleri',
        iso: 'ISO 9001:2015 Sertifikalı',
        language: 'Dil',
        links: { corporate: '/kurumsal', blog: '/blog' },
        serviceLinks: [
            { label: 'Tampon Baskı', to: '/tampon-baski' },
            { label: 'Serigrafi Baskı', to: '/serigrafi' },
            { label: 'Montaj & Trim', to: '/montaj-trim' },
        ],
    },
    en: {
        companyDesc: '25+ years of expertise in automotive supply chain — pad printing, screen printing, and assembly solutions.',
        quickLinks: 'Quick Links',
        services: 'Services',
        about: 'About Us',
        corporate: 'Corporate',
        blog: 'Blog/News',
        solutions: 'Solutions',
        contactTitle: 'Contact',
        address: 'Küçükbalıklı Mah. 2. Ordulu Sok. No:83',
        city: 'Osmangazi / Bursa, Türkiye',
        copyright: '© 2026 Ankaplastik. All rights reserved.',
        cookiePrefs: 'Cookie preferences',
        iso: 'ISO 9001:2015 Certified',
        language: 'Language',
        links: { corporate: '/en/corporate', blog: '/en/blog' },
        serviceLinks: [
            { label: 'Pad Printing', to: '/en/pad-printing' },
            { label: 'Screen Printing', to: '/en/screen-printing' },
            { label: 'Assembly & Trim', to: '/en/assembly-trim' },
        ],
    },
    de: {
        companyDesc: '25+ Jahre Erfahrung in der Automobilzulieferung — Tampondruck, Siebdruck und Montagelösungen.',
        quickLinks: 'Schnelllinks',
        services: 'Leistungen',
        about: 'Über Uns',
        corporate: 'Unternehmen',
        blog: 'Blog/Nachrichten',
        solutions: 'Lösungen',
        contactTitle: 'Kontakt',
        address: 'Küçükbalıklı Mah. 2. Ordulu Sok. No:83',
        city: 'Osmangazi / Bursa, Türkiye',
        copyright: '© 2026 Ankaplastik. Alle Rechte vorbehalten.',
        cookiePrefs: 'Cookie-Einstellungen',
        iso: 'ISO 9001:2015 Zertifiziert',
        language: 'Sprache',
        links: { corporate: '/de/unternehmen', blog: '/de/blog' },
        serviceLinks: [
            { label: 'Tampondruck', to: '/de/tampondruck' },
            { label: 'Siebdruck', to: '/de/siebdruck' },
            { label: 'Montage & Trim', to: '/de/montage-trim' },
        ],
    },
}

const LANG_SHORT = [
    ['tr', 'TR', 'Türkçe'],
    ['en', 'EN', 'English'],
    ['de', 'DE', 'Deutsch'],
]

/**
 * The logo's halftone cascade, redrawn large: the squares thin out toward the
 * corner the way the dots do in the wordmark. Decorative.
 */
function Cascade() {
    const cells = []
    const size = 10
    for (let row = 0; row < size; row += 1) {
        for (let col = 0; col < size; col += 1) {
            const weight = (col + (size - row)) / (2 * size)
            if (weight < 0.52) continue
            const s = 4 + Math.round((weight - 0.52) * 30)
            const orange = (row * 3 + col * 5) % 4 !== 0
            cells.push(
                <rect
                    key={`${row}-${col}`}
                    x={col * 24 + (24 - s) / 2}
                    y={row * 24 + (24 - s) / 2}
                    width={s}
                    height={s}
                    rx="1"
                    className={orange ? 'cascade-o' : 'cascade-b'}
                />
            )
        }
    }
    return (
        <svg className="footer-cascade" viewBox="0 0 240 240" aria-hidden="true" focusable="false">
            {cells}
        </svg>
    )
}

export default function Footer({ lang = 'tr' }) {
    const t = translations[lang] || translations.tr
    const { pathname } = useLocation()
    const path = normalizePath(pathname)
    const home = HOME_BY_LANG[lang] || '/'
    const onHome = path === normalizePath(home)
    const homeAnchor = (hash) => {
        if (onHome) return hash
        return home === '/' ? `/${hash}` : `${home}${hash}`
    }
    const langLinks = alternatesFor(path) ?? HOME_BY_LANG

    return (
        <footer className="footer polymer">
            <div className="container">
                <div className="footer-top">
                    <div className="footer-brand">
                        <Link to={home} className="footer-label" aria-label="Ankaplastik">
                            <img
                                src="/anka-logo-full-220w.webp"
                                alt="Ankaplastik Logo"
                                className="footer-logo-image"
                                width="190"
                                height="56"
                                loading="lazy"
                            />
                        </Link>
                        <p className="footer-desc">{t.companyDesc}</p>
                        <p className="footer-iso legend">{t.iso}</p>
                    </div>
                    <Cascade />
                </div>

                <div className="footer-grid">
                    <nav className="footer-col" aria-labelledby="footer-solutions">
                        <h2 className="footer-heading legend" id="footer-solutions">{t.solutions}</h2>
                        <ul className="footer-links">
                            {t.serviceLinks.map((item) => (
                                <li key={item.to}>
                                    <Link to={item.to} viewTransition>{item.label}</Link>
                                </li>
                            ))}
                        </ul>
                    </nav>

                    <nav className="footer-col" aria-labelledby="footer-quick">
                        <h2 className="footer-heading legend" id="footer-quick">{t.quickLinks}</h2>
                        <ul className="footer-links">
                            <li><a href={homeAnchor('#services')}>{t.services}</a></li>
                            <li><a href={homeAnchor('#about')}>{t.about}</a></li>
                            <li><Link to={t.links.corporate} viewTransition>{t.corporate}</Link></li>
                            <li><Link to={t.links.blog} viewTransition>{t.blog}</Link></li>
                        </ul>
                    </nav>

                    <div className="footer-col footer-contact">
                        <h2 className="footer-heading legend">{t.contactTitle}</h2>
                        <ul className="footer-links">
                            <li>
                                <a href={MAPS_URL} target="_blank" rel="noopener noreferrer" className="footer-address">
                                    <span>{t.address}<br />{t.city}</span>
                                    <IconArrowUpRight size={16} />
                                </a>
                            </li>
                            <li><a href={PHONE_HREF} className="tabular">{PHONE_DISPLAY}</a></li>
                            <li><a href={`mailto:${EMAIL}`}>{EMAIL}</a></li>
                        </ul>
                    </div>
                </div>

                <div className="footer-bottom">
                    <span className="footer-copyright">
                        {t.copyright}
                        <button
                            type="button"
                            className="footer-consent"
                            onClick={() => window.dispatchEvent(new Event(CONSENT_REOPEN_EVENT))}
                        >
                            {t.cookiePrefs}
                        </button>
                    </span>
                    <div className="footer-bottom-end">
                        <nav aria-label={t.language}>
                            <ul className="footer-langs">
                                {LANG_SHORT.map(([code, short, name]) => (
                                    <li key={code}>
                                        {code === lang ? (
                                            <span aria-current="true" title={name}>{short}</span>
                                        ) : (
                                            <Link to={langLinks[code]} hrefLang={code} lang={code} title={name}>{short}</Link>
                                        )}
                                    </li>
                                ))}
                            </ul>
                        </nav>
                        <div className="footer-social">
                            <a href="https://www.linkedin.com/company/ankaplastik" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn" className="social-icon">
                                <IconLinkedIn size={18} />
                            </a>
                            <a href="https://www.instagram.com/ankaplastik" target="_blank" rel="noopener noreferrer" aria-label="Instagram" className="social-icon">
                                <IconInstagram size={18} />
                            </a>
                        </div>
                    </div>
                </div>
            </div>
        </footer>
    )
}
